import React, {useState} from "react";
import {ChevronRightIcon, UserPlusIcon} from "@heroicons/react/24/outline";
import {ApprovalType} from "../../../../constant/CodeSpecNum";
import {UserChipAvatar} from "../../../../components/selection/UserSelectionChips";
import {isProxyApprovalAction, clusterApprovalRows, countApprovalMembers} from "./approvalUtils";
import "../../../../components/selection/userSelectionChips.css";
import "./approvalComponent.css";

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getRowStatus(item, activeGroupId) {
    if (activeGroupId != null && item.groupId === activeGroupId && item.approvalType === ApprovalType.PENDING) {
        return "pending";
    }
    if (item.approvalType === ApprovalType.APPROVED) {
        return "approved";
    }
    if (item.approvalType === ApprovalType.REJECTED) {
        return "rejected";
    }
    return "";
}

function resolveDisplayStep(index) {
    return index + 1;
}

function getApprovalResultLabel(item, status) {
    if (item.isPassed) return "전결";
    if (item.approvalType === ApprovalType.APPROVED) return "승인";
    if (item.approvalType === ApprovalType.REJECTED) return "반려";
    if (status === "pending") return "진행 중";
    return "대기";
}

function buildChipDetails(item, status, step) {
    const isDone =
        item.approvalType === ApprovalType.APPROVED ||
        item.approvalType === ApprovalType.REJECTED ||
        item.isPassed;

    return {
        title: `${step}차 결재`,
        isProxyApproval: isProxyApprovalAction(item),
        rows: [
            {label: "수신자", value: item.actor?.name ?? "—"},
            {
                label: "결재자",
                value: isDone ? (item.approvedUserName || item.actor?.name || "—") : "—",
            },
            {label: "결재 결과", value: getApprovalResultLabel(item, status)},
            {
                label: "결재 시간",
                value: item.updatedAt ? formatDateTime(item.updatedAt) : "—",
            },
            {
                label: "결재 의견",
                value: item.description?.trim() ? item.description : "—",
            },
        ],
    };
}

function ApprovalStatusMark({status}) {
    if (status === "approved") {
        return <span className="approval-comp__chip-badge approval-comp__chip-badge--approve">승인</span>;
    }
    if (status === "rejected") {
        return <span className="approval-comp__chip-badge approval-comp__chip-badge--reject">반려</span>;
    }
    if (status === "pending") {
        return <span className="approval-comp__chip-badge approval-comp__chip-badge--pending">진행</span>;
    }
    return null;
}

function ApprovalStepDetail({details}) {
    return (
        <div className="approval-comp__chip-detail" role="tooltip">
            <div className="approval-comp__chip-detail-head">
                <p className="approval-comp__chip-detail-title">{details.title}</p>
                {details.isProxyApproval ? (
                    <span className="approval-comp__chip-badge approval-comp__chip-badge--proxy">대결</span>
                ) : null}
            </div>
            <dl className="approval-comp__chip-detail-list">
                {details.rows.map((row) => (
                    <div key={row.label} className="approval-comp__chip-detail-row">
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function getClusterStatus(cluster, activeGroupId) {
    const representative = cluster.members?.[0];
    if (!representative) {
        return "";
    }
    return getRowStatus({...representative, groupId: cluster.groupId}, activeGroupId);
}

function ApprovalGroupBundle({cluster, status, step}) {
    const groupLabel = cluster.groupName?.trim();

    return (
        <div
            className={[
                "approval-comp__group-bundle",
                cluster.isGroupBundle ? "approval-comp__group-bundle--multi" : "",
                status ? `approval-comp__group-bundle--${status}` : "",
            ].filter(Boolean).join(" ")}
        >
            <div className="approval-comp__group-bundle-body">
                <div className="approval-comp__group-bundle-head">
                    <span className="approval-comp__group-bundle-step">{step}차</span>
                    {groupLabel ? (
                        <span className="approval-comp__group-bundle-label">{groupLabel}</span>
                    ) : null}
                    <ApprovalStatusMark status={status} />
                </div>
                <div className="approval-comp__group-bundle-members">
                    {(cluster.members ?? []).map((member) => {
                        const chipItem = {
                            name: member.actor?.name ?? "결재자",
                            profileImage: member.actor?.profileImage,
                        };

                        return (
                            <span
                                key={member.rowKey}
                                className={[
                                    "user-selection-chip",
                                    "approval-comp__chip",
                                    "approval-comp__chip--member",
                                    member.isPassed ? "approval-comp__chip--passed" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                <UserChipAvatar item={chipItem} />
                                <span className="user-selection-chip-name">{chipItem.name}</span>
                                {member.department ? (
                                    <span className="approval-comp__chip-dept">{member.department}</span>
                                ) : null}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function ApprovalStepChip({item, status, step, showChipDetail = true, onChipClick}) {
    const [hovered, setHovered] = useState(false);
    const details = buildChipDetails(item, status, step);
    const chipItem = {
        name: item.actor?.name ?? "결재자",
        profileImage: item.actor?.profileImage,
    };

    const chipClassName = [
        "user-selection-chip",
        "approval-comp__chip",
        status ? `approval-comp__chip--${status}` : "",
    ].filter(Boolean).join(" ");

    const chipContent = (
        <>
            <UserChipAvatar item={chipItem} />
            <span className="user-selection-chip-name">{chipItem.name}</span>
            <ApprovalStatusMark status={status} />
            <span className="approval-comp__chip-step">{step}차</span>
        </>
    );

    if (!showChipDetail) {
        return (
            <span className="approval-comp__chip-wrap">
                <span className={chipClassName}>{chipContent}</span>
            </span>
        );
    }

    return (
        <span
            className={[
                "approval-comp__chip-wrap",
                hovered ? "approval-comp__chip-wrap--open" : "",
            ].filter(Boolean).join(" ")}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <button
                type="button"
                className={chipClassName}
                onClick={(event) => onChipClick?.(item, event)}
                aria-expanded={hovered}
                aria-label={`${step}차 결재 ${chipItem.name} 상세`}
            >
                {chipContent}
            </button>
            {hovered ? <ApprovalStepDetail details={details} /> : null}
        </span>
    );
}

function ApprovalEmpty({label}) {
    return (
        <div className="approval-comp__empty">
            <UserPlusIcon className="approval-comp__empty-icon" aria-hidden />
            <p className="approval-comp__empty-label">{label}</p>
        </div>
    );
}

/**
 * gov_front ApproversComponent 와 동일한 결재자 목록 UI
 */
export default function ApprovalList({
    rows,
    activeGroupId,
    emptyLabel = "결재자를 선택해주세요",
    className = "",
    showChipDetail = true,
    onChipClick,
}) {
    const clusters = clusterApprovalRows(rows);
    const memberCount = countApprovalMembers(clusters);

    if (clusters.length === 0) {
        return <ApprovalEmpty label={emptyLabel} />;
    }

    return (
        <div className="approval-comp__list-wrap">
            <div className="approval-comp__list-toolbar">
                <span className="user-selection-chips-count">
                    총 {clusters.length}단계 · {memberCount}명
                </span>
            </div>
            <div className={`approval-comp__list user-selection-chips-list ${className}`.trim()}>
                {clusters.map((cluster, index) => {
                    const step = resolveDisplayStep(index);
                    const status = getClusterStatus(cluster, activeGroupId);
                    const singleMember = cluster.members?.[0];

                    return (
                        <React.Fragment key={cluster.clusterKey}>
                            {index > 0 ? (
                                <span className="approval-comp__flow-arrow" aria-hidden>
                                    <ChevronRightIcon />
                                </span>
                            ) : null}
                            {cluster.isGroupBundle ? (
                                <ApprovalGroupBundle cluster={cluster} status={status} step={step} />
                            ) : (
                                <ApprovalStepChip
                                    item={singleMember}
                                    status={status}
                                    step={step}
                                    showChipDetail={showChipDetail}
                                    onChipClick={onChipClick}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
